<template>
  <section class="services-section">
    <v-container>
      <!-- Section Header -->
      <div class="section-header animate-block">
        <div class="section-title-wrapper">
          <div class="title-border"></div>
          <div class="title-content">
            <h2 class="section-title">{{ $t('services.headerTitle') }}</h2>
            <p class="section-subtitle">{{ $t('services.headerSubtitle') }}</p>
          </div>
        </div>
      </div>

      <!-- Three Columns Description -->
      <v-row class="description-row animate-block">
        <v-col cols="12" md="4" class="pr-md-12">
          <p class="description-text">
            <span class="highlight">{{ $t('services.col1Highlight') }}</span> {{ $t('services.col1') }}
          </p>
        </v-col>
        <v-col cols="12" md="4" class="pr-md-12">
          <p class="description-text">
            <span class="highlight">{{ $t('services.col2Highlight') }}</span> {{ $t('services.col2') }}
          </p>
        </v-col>
        <v-col cols="12" md="4" class="pr-md-12">
          <p class="description-text">
            <span class="highlight">{{ $t('services.col3Highlight') }}</span> {{ $t('services.col3') }}
          </p>
        </v-col>
      </v-row>

      <!-- Central Image -->
      <div class="central-image-wrapper animate-block">
        <img src="../assets/images/3.webp" alt="Insurance Services" class="central-image" />
      </div>

      <!-- Divider -->
      <div class="section-divider"></div>

      <!-- Services Tabs -->
      <div class="services-tabs-wrapper animate-block">
        <span class="tab-dot">●</span>
        <span class="tab-text">{{ $t('services.sectionLabel') }} {{ $t('services.forLabel') }} <span class="tab-gray">{{ $t('services.labelPerson') }}</span> {{ $t('services.andLabel') }} <span class="tab-gray">{{ $t('services.labelBusiness') }}</span></span>
      </div>

      <!-- Services Grid -->
      <v-row class="services-grid" id="services">
        <v-col v-for="(service, index) in translatedServices" :key="index" cols="12" md="6" class="animate-block">
          <div class="service-card" @click="openService(service)">
            <!-- Card Header -->
            <div class="card-header">
              <!-- Default state -->
              <span class="service-number default-label">{{ String(index + 1).padStart(2, '0') }}</span>
              <div class="expand-btn">
                <span class="expand-btn-text">+</span>
              </div>
            </div>
            <!-- Card Body -->
            <v-row no-gutters align="center" class="card-body">
              <v-col class="card-text">
                <h3 class="service-title">{{ service.title }}</h3>
                <p class="service-description">{{ service.description }}</p>
              </v-col>
              <v-col cols="auto" class="card-image-col">
                <picture>
                <source media="(max-width: 600px)" :srcset="service.mobileImage" />
                <img :src="service.image" :alt="service.title" class="service-img" :style="{ '--aspect-ratio': service.aspectRatio, '--mobile-aspect-ratio': service.mobileAspectRatio }" />
              </picture>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <!-- Bottom CTA -->
      <div class="bottom-cta">
        <p class="cta-text">
          <span class="cta-dot">●</span> {{ $t('services.ctaText') }} <a href="#" class="cta-link" @click.prevent="openContact">{{ $t('services.ctaLink') }}</a>
        </p>
      </div>
    </v-container>
  </section>
</template>


<script>
import { eventBus } from '@/eventBus'
import { animateOnScroll } from '@/mixins/animateOnScroll'

export default {
  name: 'ServicesSection',
  mixins: [animateOnScroll],
  computed: {
    translatedServices() {
      const keys = ['rcai', 'rcae', 'property', 'casco', 'accident', 'travelMedical', 'medical', 'cargo', 'industrial', 'transport', 'professional']
      return this.servicesBase.map((s, i) => ({
        ...s,
        title: this.$t(`services.items.${keys[i]}.title`),
        description: this.$t(`services.items.${keys[i]}.description`),
        modalDescription: this.$t(`services.items.${keys[i]}.modalDescription`)
      }))
    }
  },
  methods: {
    openContact() {
      eventBus.showContactModal = true
    },
    openService(service) {
      eventBus.serviceModalData = { title: service.title, description: service.description, modalDescription: service.modalDescription }
      eventBus.showServiceModal = true
    },
    toRoman(num) {
      const vals = [10, 9, 5, 4, 1]
      const syms = ['X', 'IX', 'V', 'IV', 'I']
      let result = ''
      for (let i = 0; i < vals.length; i++) {
        while (num >= vals[i]) {
          result += syms[i]
          num -= vals[i]
        }
      }
      return result
    }
  },
  data() {
    return {
      servicesBase: [
        {
          title: 'RCAI – Asigurare obligatorie de răspundere civilă auto (internă)',
          description: 'Asigurătorul despăgubește daunele cauzate bunurilor sau sănătății persoanelor terțe, în caz că sunteți declarat vinovat de producerea accidentului în traficul rutier.',
          image: require('@/assets/images/3.ServicesSection/1.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/1.webp'),
          aspectRatio: '336 / 183',
          mobileAspectRatio: '352 / 336',
          modalDescription: '<p><b>Polița de asigurare RCA</b> acoperă prejudiciul cauzat vieții, sănătății și bunurilor materiale a persoanelor terțe.</p><p><b>Asigurătorul despăgubește</b> daunele cauzate bunurilor sau sănătății persoanelor terțe, în caz că sunteți declarat vinovat de producerea accidentului în traficul rutier.</p>'
        },
        {
          title: 'RCAE – Asigurare obligatorie externă (Carte Verde)',
          description: 'Asigurare obligatorie pentru deplasarea peste hotare, care acoperă prejudiciile produse terților.',
          image: require('@/assets/images/3.ServicesSection/2.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/2.webp'),
          aspectRatio: '323 / 300',
          mobileAspectRatio: '334 / 224',
          modalDescription: '<p><b>Cartea Verde</b> este o asigurare obligatorie pentru toți proprietarii / utilizatorii de autovehicule care pleacă peste hotare. Acoperă prejudiciul material și daunele cauzate sănătății/vieții terțelor persoane, ca urmare a producerii accidentelor rutiere produse în afara hotarelor Republicii Moldova.</p>'
        },
        {
          title: 'Asigurarea bunurilor imobile și mobile',
          description: 'Ce acoperă asigurarea bunurilor? Pagube cauzate de incendiu, furt, inundații, calamități naturale sau vandalism.',
          image: require('@/assets/images/3.ServicesSection/3.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/3.webp'),
          aspectRatio: '336 / 247',
          mobileAspectRatio: '503 / 339',
          modalDescription: '<p><b>Protecție pentru bunurile imobile</b></p><p>Obiectele supuse asigurării: apartament, casă de locuit, încăperi nelocative, spații comerciale.</p><p><b>Protecție pentru bunurile mobile</b></p><p>Obiectele supuse asigurării: mobilă, tehnică, utilaj.</p><p><b>Protecție în următoarele cazuri:</b></p><p>Pagube cauzate de inundație, alunecare de teren, incendiu, vandalism, furt, avarii accidentale ale instalațiilor de apă.</p>'
        },
        {
          title: 'CASCO – Asigurare facultativă auto',
          description: 'De ce am nevoie de CASCO? Oferă protecție extinsă indiferent de vinovăție.',
          image: require('@/assets/images/3.ServicesSection/4.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/4.webp'),
          aspectRatio: '335 / 231',
          mobileAspectRatio: '337 / 306',
          modalDescription: '<p><b>CASCO oferă protecție extinsă</b></p><p>Acoperă daunele cauzate vehiculului tău în cazuri de accident, furt, fenomene naturale sau vandalism și daune parțiale:</p><p>prejudiciile (daunele) materiale cauzate Asiguratului prin deteriorarea sau distrugerea mijlocului de transport și a echipamentului suplimentar ca urmare a accidentelor de circulație, fenomene naturale, acțiuni intenționate ale altor persoane, vandalism; furtul, jaful, tâlhăria, răpirea mijlocului de transport, a părților componente sau pieselor acestuia, echipamentului primit în asigurare;</p><p>acoperă leziunile corporale și decesul șoferului / pasagerilor aflați în mijlocul de transport asigurat în momentul survenirii cazurilor de accident, furt, fenomene naturale sau vandalism și daune parțiale.</p>'
        },
        {
          title: 'Asigurare de accidente',
          description: 'Ce riscuri sunt acoperite? Vătămări corporale, intoxicații, dizabilitate, deces.',
          image: require('@/assets/images/3.ServicesSection/5.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/5.webp'),
          aspectRatio: '333 / 332',
          mobileAspectRatio: '248 / 218',
          modalDescription: '<p><b>Asigurarea de accidente</b> este cea mai bună alegere pentru sănătatea ta și a familiei tale, urmare cheltuielilor neprevăzute provocate de un accident soldat cu leziuni ușoare sau traume.</p><p><b>Acoperire Extinsă</b></p><p>— Asigurare destinată persoanelor fizice;<br>— Asigurarea de accidente a angajaților;<br>— Asigurare destinată conducătorilor auto.</p><p><b>Produs individual pentru familie</b></p><p>Acoperirea pentru traume și accidente 24/7, inclusiv copii.</p>'
        },
        {
          title: 'Asigurare medicală pentru călătorii în străinătate',
          description: 'Sunt acoperite tratament ambulator și staționar, medicamente, transport medical, repatriere.',
          image: require('@/assets/images/3.ServicesSection/6.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/6.webp'),
          aspectRatio: '329 / 335',
          mobileAspectRatio: '274 / 274',
          modalDescription: '<p><b>Polița de asigurare medicală peste hotare</b> vă oferă acoperire de asigurare și vă compensează cheltuielile pentru serviciile medicale, în caz de o eventuală îmbolnăvire sau accident.</p><p><b>Siguranță în orice colț al lumii</b></p><p>Vă oferim polița de asigurare pentru acoperire mondială.</p><p><b>Abordare individuală la perfectarea Poliței</b></p><p>Polița este emisă luând în considerare scopul călătoriei, perioada de asigurare, țara de destinație, cu excepția R.Moldova — teritoriul în cadrul căruia domiciliază Asiguratul și în țările în care are domiciliul stabil sau al cărui cetățean este (cazuri de dubla cetățenie).</p><p><b>Asistență profesională</b></p><p>Compania de asistență care reprezintă peste hotare Asigurătorul deține contracte de colaborare cu instituțiile medicale din întreaga lume și oferă consultanță în regim 24/7.</p>'
        },
        {
          title: 'Asigurare medicală facultativă',
          description: 'Maladii acute, agravări ale bolilor cronice, accidente.',
          image: require('@/assets/images/3.ServicesSection/7.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/7.webp'),
          aspectRatio: '290 / 252',
          mobileAspectRatio: '345 / 345',
          modalDescription: '<p><b>Asigurarea medicală</b> facultativă oferă posibilitatea de a beneficia de îngrijire medicală adecvată, precum și de a avea control asupra propriei sănătăți.</p><p><b>Acces la asistență medicală</b></p><p>Asigurarea medicală facultativă oferă acces rapid la medici, investigații și tratamente.</p><p><b>Abordare complexă</b></p><p>Programele de asigurare acoperă un spectru larg de servicii medicale de ambulator și staționar.</p><p><b>Flexibilitate în deservire medicală</b></p><p>Posibilitatea de a alege una din instituțiile medicale indicate în Contract.</p>'
        },
        {
          title: 'Asigurare CARGO',
          description: 'Distrugerea sau deteriorarea totală/parțială a mărfurilor.',
          image: require('@/assets/images/3.ServicesSection/8.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/8.webp'),
          aspectRatio: '337 / 223',
          mobileAspectRatio: '434 / 340',
          modalDescription: '<p>Asigurare pentru proprietarul de încărcături, contra eventualelor deteriorări sau distrugeri.</p><p><b>Acoperire în timpul transportării</b></p><p>Asigurare pentru o transportare unică sau multiplă a mărfurilor pe cale terestră, feroviară, navală și aeriană.</p><p><b>Acoperire complexă</b></p><p>Compania încheie contracte pentru diverse bunuri transportate pe cale terestră, feroviară, navală și aeriană.</p>'
        },
        {
          title: 'Asigurarea obiectelor industrial periculoase',
          description: 'Este obligatorie sau benevol? Obligatorie conform Legii nr. 116/2012.',
          image: require('@/assets/images/3.ServicesSection/9.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/9.webp'),
          aspectRatio: '333 / 330',
          mobileAspectRatio: '440 / 431',
          modalDescription: '<p>Sunt acoperite pagube sau pierderi cauzate de incendii, furturi, inundații și alte calamități.</p><p><b>Cine apreciază costul pentru care se asigură bunul imobil?</b></p><p>Contractul de asigurare se încheie pentru un obiect asigurat la o sumă care nu poate depăși valoarea de piață.</p><p><b>Ce acte sunt necesare pentru perfectarea Contractului de asigurare de BUNURI?</b></p><p>Pentru încheierea unui Contract este nevoie de:<br>— buletinul de identitate al proprietarului (persoana fizică), certificatul de înregistrare (persoană juridică);<br>— extras din Registrul de Stat al persoanelor juridice;<br>— extras din Registrul de Stat a bunurilor imobile (persoane fizice și juridice);<br>— actele ce confirmă valoarea bunurilor propuse spre asigurare: Extras Cadastral, Raport de evaluare, Contracte de vânzare-cumpărare, alte acte la cerere.</p><p><b>Este necesară asigurarea separată pentru bunurile imobile și obiectele casnice?</b></p><p>Pentru bunurile imobile și bunurile mobile (obiecte personale) sunt emise polițe de asigurare separate.</p>'
        },
        {
          title: 'Asigurarea de răspundere civilă a transportatorilor față de călători',
          description: 'Vătămări corporale și distrugerea bunurilor călătorilor.',
          image: require('@/assets/images/3.ServicesSection/10.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/10.webp'),
          aspectRatio: '336 / 283',
          mobileAspectRatio: '434 / 431',
          modalDescription: '<p><b>Ce acoperă asigurarea pentru bunuri imobile și mobile?</b></p><p>Sunt acoperite pagube sau pierderi cauzate de incendii, furturi, inundații și alte calamități.</p><p><b>Cine apreciază costul pentru care se asigură bunul imobil?</b></p><p>Contractul de asigurare se încheie pentru un obiect asigurat la o sumă care nu poate depăși valoarea de piață.</p><p><b>Ce acte sunt necesare pentru perfectarea Contractului de asigurare de BUNURI?</b></p><p>Pentru încheierea unui Contract este nevoie de:<br>— buletinul de identitate al proprietarului (persoana fizică), certificatul de înregistrare (persoană juridică);<br>— extras din Registrul de Stat al persoanelor juridice;<br>— extras din Registrul de Stat a bunurilor imobile (persoane fizice și juridice);<br>— actele ce confirmă valoarea bunurilor propuse spre asigurare: Extras Cadastral, Raport de evaluare, Contracte de vânzare-cumpărare, alte acte la cerere.</p><p><b>Este necesară asigurarea separată pentru bunurile imobile și obiectele casnice?</b></p><p>Pentru bunurile imobile și bunurile mobile (obiecte personale) sunt emise polițe de asigurare separate.</p>'
        },
        {
          title: 'Asigurarea răspunderii civile profesionale (RCP)',
          description: 'Orice entitate licențiată poate încheia polița. Este obligatorie pentru brokeri, anual, conform Legii nr. 92/2022. Sunt acoperite prejudicii aduse clienților din erori profesionale.',
          image: require('@/assets/images/3.ServicesSection/11.webp'),
          mobileImage: require('@/assets/images/3.ServicesSectionMobile/11.webp'),
          aspectRatio: '338 / 271',
          mobileAspectRatio: '411 / 383',
          modalDescription: '<p>Asigurătorul acoperă prejudiciile provocate de Asigurat pentru desfășurarea activității indicată în Polița de asigurare, ca urmare a unor acte de neglijentă, erori sau omisiuni săvârșite în mod involuntar, prin care <b>devine răspunzător în virtutea activității sale profesionale.</b></p><p>În exercitarea profesiei Asiguratului, dacă prin fapta sa, încalcă o anumită obligație contractuală prin care se produc prejudicii altor persoane, răspunderea este antrenată atât pentru fapta proprie, cât și pentru faptele celor pentru care Asiguratul răspunde în fața legii.</p><p><b>Acoperirea prejudiciului față de clienți</b></p><p>Asigurarea acoperă prejudiciile aduse în activitatea profesională față de clienții Asiguratului.</p><p><b>Acoperire Complexă</b></p><p>Oferim o acoperire specializată domeniilor în justiție (notar, executor judecătoresc, avocat, administrator), precum și domenii financiare (contabil, auditor).</p>'
        }
      ]
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400&display=swap');

.highlight {
  color: #1CA5BE;
}

.services-section {
  scroll-margin-top: 126px;
  padding: 80px 0;
  background-color: #ededed;
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ===== SECTION HEADER ===== */
.section-header {
  margin-bottom: 48px;
}

.section-title-wrapper {
  display: flex;
  align-items: stretch;
  gap: 20px;
}

.title-border {
  width: 5px;
  background: #1ca5be;
  flex-shrink: 0;
}

.title-content {
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 32px;
  font-weight: 500;
  color: rgb(28, 165, 190);
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 41.6px;
}

.section-subtitle {
  font-size: 32px;
  font-weight: 500;
  color: rgb(47, 47, 47);
  margin: 0;
  letter-spacing: -0.01em;
  line-height: 41.6px;
  max-width: 520px;
}

/* ===== DESCRIPTION ROW ===== */
.description-row {
  margin-bottom: 50px;
}

.description-text {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.01em;
  color: rgb(47, 47, 47);
  margin: 0;
}

/* ===== CENTRAL IMAGE ===== */
.central-image-wrapper {
  width: 100%;
  max-width: 700px;
  margin: 0 auto 40px;
  overflow: hidden;
}

.central-image {
  width: 100%;
  height: auto;
  display: block;
}

/* ===== DIVIDER ===== */
.section-divider {
  width: 60%;
  max-width: 800px;
  height: 1px;
  background: #0000001A;
  margin: 0 auto 60px;
}

/* ===== SERVICES TABS ===== */
.services-tabs-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 40px;
}

.tab-dot {
  font-family: "SF Pro Display", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgb(47, 47, 47);
  line-height: 30px;
  letter-spacing: -0.01em;
}

.tab-text {
  font-family: "SF Pro Display", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #2F2F2F;
  line-height: 30px;
  letter-spacing: -0.01em;
}

.tab-gray {
  color: #7D7D7D;
}

/* ===== SERVICES GRID ===== */
.services-grid {
  scroll-margin-top: 126px;
  margin-bottom: 50px;
}

.service-card {
  background: #e7e7e7;
  border-radius: 4px;
  padding: 5px 0px 9px 22px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 60px;
  overflow: hidden;
  transition: background 0.4s ease;
  cursor: pointer;
}

/* ===== CARD HEADER ===== */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 5px;
}

.service-number {
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 400;
  color: rgb(47, 47, 47);
  line-height: 28.6px;
  letter-spacing: -0.01em;
  text-transform: uppercase;
}

/* Default / Hover label toggling */

.expand-btn {
  display: flex;
  width: 38px;
  height: 38px;
  background: #1ca5be;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.expand-btn-text {
  font-family: "IBM Plex Mono", monospace;
  font-size: 20px;
  font-weight: 300;
  color: rgb(241, 241, 241);
  line-height: 28.6px;
  letter-spacing: -0.01em;
  text-transform: uppercase;
}


/* ===== CARD BODY ===== */
.card-body {
  gap: 16px;
}

.card-text {
  min-width: 0;
}

.service-title {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.01em;
  color: rgb(47, 47, 47);
  margin: 0 0 9px 0;
  transition: color 0.3s ease;
}

.service-description {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.01em;
  color: rgb(47, 47, 47);
  margin: 0;
  transition: color 0.3s ease;
}

.card-image-col {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 35%;
}

.card-image-col picture {
  display: block;
  line-height: 0;
  width: 100%;
}

.service-img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  object-position: center;
  aspect-ratio: var(--aspect-ratio);
}

/* ===== HOVER STATE ===== */
.service-card:hover {
  background: #1FB1C1;
}

.service-card:hover .service-title {
  color: rgb(241, 241, 241);
}

.service-card:hover .service-description {
  color: rgb(241, 241, 241);
}

.service-card:hover .expand-btn {
  background: #ffffff;
}

.service-card:hover .expand-btn-text {
  color: #1FB1C1;
}

.service-card:hover .service-number {
  color: rgb(241, 241, 241);
}

/* ===== BOTTOM CTA ===== */
.bottom-cta {
  text-align: center;
  padding: 30px 0;
}

.cta-text {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgb(47, 47, 47);
  margin: 0;
  letter-spacing: -0.01em;
}

.cta-dot {
  color: rgb(47, 47, 47);
  margin-right: 4px;
}

.cta-link {
  color: rgb(28, 165, 190);
  text-decoration: underline;
  font-weight: 400;
}

.cta-link:hover {
  color: rgb(38, 114, 130);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 960px) {
  .services-section {
    padding: 60px 0;
  }

  .section-title,
  .section-subtitle {
    font-size: 26px;
    line-height: 34px;
  }
}

@media (max-width: 600px) {
  .services-section {
    padding: 40px 0;
  }

  .section-title,
  .section-subtitle {
    font-size: 20px;
    line-height: 28px;
  }

  .section-title-wrapper {
    gap: 14px;
  }

  .central-image-wrapper {
    max-width: 400px;
  }

  .services-tabs-wrapper {
    display: none;
  }

  .section-divider {
    display: none;
  }

  .card-body {
    flex-direction: column-reverse;
  }

  .card-image-col {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .card-image-col picture {
    width: 60%;
  }

  .service-img {
    width: 100%;
    max-width: 100%;
    aspect-ratio: var(--mobile-aspect-ratio);
  }

  .card-text {
    width: 100%;
  }

  .service-card {
    gap: 0;
    padding: 10px 9px;
  }
}
</style>
